'use client';
import React, { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import HomeButton from '@/components/HomeButton';
import { Package, Car, Home, Briefcase, Hotel, Utensils, Pill, Calendar, Truck, Video, Wrench, Newspaper, ChevronLeft, ChevronRight, Check, Upload, X, MapPin, Send, Loader2, CheckCircle, Heart, FileText, Building2, Plus, AlertCircle, Camera, Shield, Star, Scissors } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

type ListingType =
'product' | 'vehicle' | 'property' | 'job' | 'hotel' | 'food' | 'pharmacy' | 'event' | 'transport' | 'video' | 'service' | 'news' | 'classified' | 'dating' | 'beauty_fashion' | 'business_directory';

interface ListingCategory {
  id: ListingType;
  label: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  roles?: string[];
}

const LISTING_CATEGORIES: ListingCategory[] = [
{ id: 'product', label: 'Sell Product', icon: <Package size={22} />, color: '#00C853', description: 'List items for sale', roles: ['seller', 'buyer'] },
{ id: 'classified', label: 'Post Classified', icon: <FileText size={22} />, color: '#69F0AE', description: 'Quick classified ad', roles: ['buyer', 'seller'] },
{ id: 'vehicle', label: 'Sell Vehicle', icon: <Car size={22} />, color: '#2196F3', description: 'Cars, bikes, trucks', roles: ['seller'] },
{ id: 'property', label: 'Post Property', icon: <Home size={22} />, color: '#FF9800', description: 'Sale or rent', roles: ['agent', 'seller'] },
{ id: 'job', label: 'Post Job', icon: <Briefcase size={22} />, color: '#9C27B0', description: 'Hire talent', roles: ['employer', 'agent'] },
{ id: 'hotel', label: 'List Hotel', icon: <Hotel size={22} />, color: '#F44336', description: 'Rooms & accommodation', roles: ['seller'] },
{ id: 'food', label: 'Add Food Item', icon: <Utensils size={22} />, color: '#FF5722', description: 'Restaurant menu', roles: ['seller'] },
{ id: 'pharmacy', label: 'Add Pharmacy Product', icon: <Pill size={22} />, color: '#00BCD4', description: 'Medical products', roles: ['seller'] },
{ id: 'beauty_fashion', label: 'Beauty & Fashion', icon: <Scissors size={22} />, color: '#E91E63', description: 'Products with variants', roles: ['seller'] },
{ id: 'dating', label: 'Dating Profile', icon: <Heart size={22} />, color: '#FF4081', description: 'Meet people', roles: ['buyer'] },
{ id: 'business_directory', label: 'Business Directory', icon: <Building2 size={22} />, color: '#5C6BC0', description: 'Service + availability', roles: ['seller', 'employer'] },
{ id: 'event', label: 'Create Event', icon: <Calendar size={22} />, color: '#E91E63', description: 'Tickets & events', roles: ['seller', 'employer'] },
{ id: 'transport', label: 'Add Transport', icon: <Truck size={22} />, color: '#607D8B', description: 'Routes & schedules', roles: ['seller'] },
{ id: 'video', label: 'Upload Video', icon: <Video size={22} />, color: '#FF1744', description: 'Share content', roles: ['buyer', 'seller'] },
{ id: 'service', label: 'Offer Service', icon: <Wrench size={22} />, color: '#795548', description: 'Skills & services', roles: ['seller', 'job_seeker'] },
{ id: 'news', label: 'Publish News', icon: <Newspaper size={22} />, color: '#3F51B5', description: 'Articles & updates', roles: ['seller'] }];


function getRecommendedTypes(role: string): ListingType[] {
  switch (role) {
    case 'seller':return ['product', 'classified', 'vehicle', 'service', 'food', 'hotel'];
    case 'employer':return ['job', 'event', 'business_directory'];
    case 'agent':return ['property', 'job'];
    case 'job_seeker':return ['service', 'classified'];
    default:return ['product', 'classified', 'vehicle', 'property', 'video', 'dating'];
  }
}

function StepProgress({ steps, current }: {steps: string[];current: number;}) {
  return (
    <div className="flex items-center gap-1 mb-6">
      {steps.map((label, i) =>
      <React.Fragment key={label}>
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 transition-all duration-300"
          style={{
            background: i < current ? 'var(--primary)' : i === current ? 'rgba(0,200,83,0.2)' : 'var(--secondary)',
            border: `2px solid ${i <= current ? 'var(--primary)' : 'var(--border)'}`,
            color: i < current ? 'var(--primary-foreground)' : i === current ? 'var(--primary)' : 'var(--muted-foreground)'
          }}>
              {i < current ? <Check size={12} /> : i + 1}
            </div>
            <span className="text-[8px] font-600 hidden sm:block" style={{ color: i === current ? 'var(--primary)' : 'var(--muted-foreground)' }}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 &&
        <div className="flex-1 h-0.5 rounded-full" style={{ background: i < current ? 'var(--primary)' : 'var(--border)' }} />
        }
        </React.Fragment>
      )}
    </div>);

}

function ImageUploadArea({ images, onAdd, onRemove, max = 6, label = 'Images'





}: {images: string[];onAdd: (url: string) => void;onRemove: (i: number) => void;max?: number;label?: string;}) {
  const demoImages = [
  'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&q=80',
  'https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=400&q=80',
  'https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=400&q=80',
  'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80',
  'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&q=80'];

  const handleAdd = () => {
    if (images.length >= max) return;
    onAdd(demoImages[images.length % demoImages.length]);
  };
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-600 text-foreground">{label} ({images.length}/{max})</label>
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) =>
        <div key={`img-${i}`} className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0"
        style={{ border: '2px solid var(--border)' }}>
            <img src={img} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
            <button onClick={() => onRemove(i)}
          className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}>
              <X size={10} style={{ color: '#fff' }} />
            </button>
          </div>
        )}
        {images.length < max &&
        <button onClick={handleAdd}
        className="w-20 h-20 rounded-xl flex flex-col items-center justify-center gap-1 flex-shrink-0 transition-all duration-150 active:scale-95"
        style={{ border: '2px dashed var(--border)', background: 'var(--secondary)' }}>
            <Upload size={18} style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-[9px] font-600" style={{ color: 'var(--muted-foreground)' }}>Add Photo</span>
          </button>
        }
      </div>
    </div>);

}

function Field({ label, children, required, hint }: {label: string;children: React.ReactNode;required?: boolean;hint?: string;}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-600 text-foreground">
        {label} {required && <span style={{ color: 'var(--danger)' }}>*</span>}
      </label>
      {children}
      {hint && <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{hint}</p>}
    </div>);

}

function Input({ placeholder, value, onChange, type = 'text', ...rest }: any) {
  return (
    <input type={type} placeholder={placeholder} value={value} onChange={onChange} {...rest}
    className="w-full px-4 py-3 rounded-xl text-sm font-500 text-foreground placeholder-muted-foreground outline-none transition-all duration-200"
    style={{ background: 'var(--input)', border: '2px solid var(--border)' }} />);

}

function Textarea({ placeholder, value, onChange, rows = 4 }: any) {
  return (
    <textarea placeholder={placeholder} value={value} onChange={onChange} rows={rows}
    className="w-full px-4 py-3 rounded-xl text-sm font-500 text-foreground placeholder-muted-foreground outline-none transition-all duration-200 resize-none"
    style={{ background: 'var(--input)', border: '2px solid var(--border)' }} />);

}

function SelectField({ value, onChange, children }: any) {
  return (
    <select value={value} onChange={onChange}
    className="w-full px-4 py-3 rounded-xl text-sm font-500 text-foreground outline-none transition-all duration-200"
    style={{ background: 'var(--input)', border: '2px solid var(--border)' }}>
      {children}
    </select>);

}

function Toggle({ checked, onChange, label }: {checked: boolean;onChange: (v: boolean) => void;label: string;}) {
  return (
    <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
      <span className="text-sm font-600 text-foreground">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className="relative w-12 h-6 rounded-full transition-all duration-200"
        style={{ background: checked ? 'var(--primary)' : 'var(--border)' }}>
        <div className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-200"
        style={{ left: checked ? '26px' : '4px' }} />
      </button>
    </div>);

}

// ─── PHARMACY FORM ────────────────────────────────────────────────────────────
function PharmacyForm({ data, onChange, images, onAddImage, onRemoveImage }: any) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: any) => onChange(key, e.target.value);
  const [prescriptionRequired, setPrescriptionRequired] = useState(data.prescriptionRequired === 'true');
  const [licenceUploaded, setLicenceUploaded] = useState(false);

  const handlePrescriptionToggle = (v: boolean) => {
    setPrescriptionRequired(v);
    onChange('prescriptionRequired', String(v));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(0,188,212,0.08)', border: '1px solid rgba(0,188,212,0.25)' }}>
        <Pill size={16} style={{ color: '#00BCD4' }} />
        <span className="text-xs font-600" style={{ color: '#00BCD4' }}>Pharmacy Product Listing — PPB Regulated</span>
      </div>

      <Field label="Product Name" required><Input placeholder="e.g. Paracetamol 500mg" value={f('name')} onChange={set('name')} /></Field>
      <Field label="Brand / Manufacturer"><Input placeholder="e.g. Dawa Limited" value={f('brand')} onChange={set('brand')} /></Field>

      <Field label="Category" required>
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select category</option>
          <option>Pain Relief</option><option>Antibiotics</option><option>Vitamins & Supplements</option>
          <option>Skin Care</option><option>Cough & Cold</option><option>Diabetes</option>
          <option>Blood Pressure</option><option>Antifungal</option><option>Eye & Ear</option>
          <option>Baby & Child</option><option>Family Planning</option><option>Other</option>
        </SelectField>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Price (KES)" required><Input type="number" placeholder="150" value={f('price')} onChange={set('price')} /></Field>
        <Field label="Stock Quantity"><Input type="number" placeholder="100" value={f('stock')} onChange={set('stock')} /></Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Dosage / Strength"><Input placeholder="e.g. 500mg, 10ml" value={f('dosage')} onChange={set('dosage')} /></Field>
        <Field label="Pack Size"><Input placeholder="e.g. 10 tablets, 100ml" value={f('packSize')} onChange={set('packSize')} /></Field>
      </div>

      <Field label="Expiry Date"><Input type="date" value={f('expiryDate')} onChange={set('expiryDate')} /></Field>

      <Toggle
        checked={prescriptionRequired}
        onChange={handlePrescriptionToggle}
        label="🩺 Prescription Required" />
      

      {prescriptionRequired &&
      <div className="p-3 rounded-xl flex items-start gap-2" style={{ background: 'rgba(255,152,0,0.08)', border: '1px solid rgba(255,152,0,0.3)' }}>
          <AlertCircle size={16} style={{ color: '#FF9800', flexShrink: 0, marginTop: 2 }} />
          <p className="text-xs" style={{ color: '#FF9800' }}>
            This product requires a valid prescription. Customers must upload a prescription before purchase. Ensure your pharmacy is licensed to dispense prescription drugs.
          </p>
        </div>
      }

      <Field label="Description / Usage Instructions">
        <Textarea placeholder="Describe the product, usage instructions, warnings..." value={f('description')} onChange={set('description')} rows={3} />
      </Field>

      <Field label="Storage Instructions"><Input placeholder="e.g. Store below 25°C, away from light" value={f('storage')} onChange={set('storage')} /></Field>

      {/* Licence Proof Upload */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-600 text-foreground">
          Pharmacy Licence Proof <span style={{ color: 'var(--danger)' }}>*</span>
        </label>
        <div className="p-4 rounded-xl flex flex-col items-center gap-3 text-center"
        style={{ border: `2px dashed ${licenceUploaded ? 'var(--primary)' : 'var(--border)'}`, background: licenceUploaded ? 'rgba(0,200,83,0.05)' : 'var(--secondary)' }}>
          {licenceUploaded ?
          <>
              <CheckCircle size={28} style={{ color: 'var(--primary)' }} />
              <div>
                <p className="text-sm font-700 text-foreground">Licence Uploaded</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>pharmacy_licence.pdf</p>
              </div>
              <button onClick={() => setLicenceUploaded(false)} className="text-xs font-600" style={{ color: 'var(--danger)' }}>Remove</button>
            </> :

          <>
              <Shield size={28} style={{ color: 'var(--muted-foreground)' }} />
              <div>
                <p className="text-sm font-600 text-foreground">Upload PPB Licence / Registration</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>PDF, JPG or PNG — max 5MB</p>
              </div>
              <button
              onClick={() => {setLicenceUploaded(true);onChange('licenceUploaded', 'true');}}
              className="px-4 py-2 rounded-xl text-sm font-700 transition-all active:scale-95"
              style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                Choose File
              </button>
            </>
          }
        </div>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Required by the Pharmacy & Poisons Board (PPB). Your listing will be reviewed before going live.
        </p>
      </div>

      <Field label="Pharmacy Name" required><Input placeholder="e.g. Goodlife Pharmacy Westlands" value={f('pharmacyName')} onChange={set('pharmacyName')} /></Field>
      <Field label="Location" required><Input placeholder="e.g. Westlands, Nairobi" value={f('location')} onChange={set('location')} /></Field>
      <Field label="Delivery Available">
        <SelectField value={f('delivery')} onChange={set('delivery')}>
          <option value="">Select</option><option>Yes</option><option>No</option>
        </SelectField>
      </Field>

      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} max={3} label="Product Images" />
    </div>);

}

// ─── FOOD FORM ────────────────────────────────────────────────────────────────
function FoodForm({ data, onChange, images, onAddImage, onRemoveImage }: any) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: any) => onChange(key, e.target.value);

  const restaurants = [
  'Java House', 'Chicken Inn', 'KFC Kenya', 'Pizza Inn', 'Steers',
  'Artcaffe', 'Galitos', 'Burger Dome', 'Mama Oliech', 'Carnivore'];


  const [customRestaurant, setCustomRestaurant] = useState(false);
  const [allergens, setAllergens] = useState<string[]>([]);
  const allergenOptions = ['Gluten', 'Dairy', 'Eggs', 'Nuts', 'Soy', 'Shellfish', 'Fish'];

  const toggleAllergen = (a: string) => {
    const updated = allergens.includes(a) ? allergens.filter((x) => x !== a) : [...allergens, a];
    setAllergens(updated);
    onChange('allergens', updated.join(','));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Restaurant Picker */}
      <Field label="Restaurant" required hint="Select your restaurant or add a new one">
        {!customRestaurant ?
        <div className="flex flex-col gap-2">
            <SelectField value={f('restaurant')} onChange={(e: any) => {
            if (e.target.value === '__custom__') {setCustomRestaurant(true);onChange('restaurant', '');} else
            set('restaurant')(e);
          }}>
              <option value="">Select restaurant</option>
              {restaurants.map((r) => <option key={r}>{r}</option>)}
              <option value="__custom__">+ Add new restaurant</option>
            </SelectField>
          </div> :

        <div className="flex gap-2">
            <Input placeholder="Enter restaurant name" value={f('restaurant')} onChange={set('restaurant')} />
            <button onClick={() => setCustomRestaurant(false)} className="px-3 py-2 rounded-xl text-xs font-600"
          style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
              Pick existing
            </button>
          </div>
        }
      </Field>

      <Field label="Menu Item Name" required><Input placeholder="e.g. Chicken Tikka Masala" value={f('name')} onChange={set('name')} /></Field>

      <Field label="Category" required>
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select category</option>
          <option>Breakfast</option><option>Lunch</option><option>Dinner</option>
          <option>Snacks</option><option>Drinks</option><option>Desserts</option>
          <option>Vegetarian</option><option>Vegan</option><option>Halal</option>
        </SelectField>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Price (KES)" required><Input type="number" placeholder="500" value={f('price')} onChange={set('price')} /></Field>
        <Field label="Prep Time (mins)" required><Input type="number" placeholder="30" value={f('prepTime')} onChange={set('prepTime')} /></Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Stock / Daily Limit"><Input type="number" placeholder="50" value={f('stock')} onChange={set('stock')} /></Field>
        <Field label="Calories (kcal)"><Input type="number" placeholder="450" value={f('calories')} onChange={set('calories')} /></Field>
      </div>

      <Field label="Description"><Textarea placeholder="Describe the dish, ingredients, flavour..." value={f('description')} onChange={set('description')} rows={3} /></Field>

      {/* Allergens */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-600 text-foreground">Allergens</label>
        <div className="flex flex-wrap gap-2">
          {allergenOptions.map((a) =>
          <button key={a} onClick={() => toggleAllergen(a)}
          className="px-3 py-1.5 rounded-full text-xs font-600 transition-all duration-150"
          style={{
            background: allergens.includes(a) ? 'rgba(255,87,34,0.15)' : 'var(--secondary)',
            border: `1px solid ${allergens.includes(a) ? '#FF5722' : 'var(--border)'}`,
            color: allergens.includes(a) ? '#FF5722' : 'var(--muted-foreground)'
          }}>
              {a}
            </button>
          )}
        </div>
      </div>

      <Field label="Spice Level">
        <SelectField value={f('spiceLevel')} onChange={set('spiceLevel')}>
          <option value="">Select</option>
          <option>Mild</option><option>Medium</option><option>Hot</option><option>Extra Hot</option>
        </SelectField>
      </Field>

      <Field label="Delivery Available">
        <SelectField value={f('delivery')} onChange={set('delivery')}>
          <option value="">Select</option><option>Yes</option><option>No</option>
        </SelectField>
      </Field>

      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} max={3} label="Food Photos" />
    </div>);

}

// ─── BEAUTY & FASHION FORM ────────────────────────────────────────────────────
function BeautyFashionForm({ data, onChange, images, onAddImage, onRemoveImage }: any) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: any) => onChange(key, e.target.value);

  const [variants, setVariants] = useState<{size: string;color: string;stock: string;price: string;}[]>([
  { size: '', color: '', stock: '', price: '' }]
  );

  const addVariant = () => setVariants((prev) => [...prev, { size: '', color: '', stock: '', price: '' }]);
  const removeVariant = (i: number) => setVariants((prev) => prev.filter((_, idx) => idx !== i));
  const updateVariant = (i: number, key: string, val: string) => {
    const updated = variants.map((v, idx) => idx === i ? { ...v, [key]: val } : v);
    setVariants(updated);
    onChange('variants', JSON.stringify(updated));
  };

  const SIZES_CLOTHING = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
  const SIZES_SHOES = ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45'];
  const COLORS = ['Black', 'White', 'Red', 'Blue', 'Green', 'Yellow', 'Pink', 'Purple', 'Brown', 'Grey', 'Navy', 'Beige'];

  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const toggleColor = (c: string) => {
    const updated = selectedColors.includes(c) ? selectedColors.filter((x) => x !== c) : [...selectedColors, c];
    setSelectedColors(updated);
    onChange('availableColors', updated.join(','));
  };

  return (
    <div className="flex flex-col gap-4">
      <Field label="Product Name" required><Input placeholder="e.g. Floral Summer Dress" value={f('title')} onChange={set('title')} /></Field>

      <Field label="Category" required>
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select category</option>
          <option>Women's Clothing</option><option>Men's Clothing</option><option>Kids' Clothing</option>
          <option>Shoes & Footwear</option><option>Bags & Accessories</option><option>Jewellery</option>
          <option>Beauty & Skincare</option><option>Hair & Wigs</option><option>Perfumes</option>
          <option>Watches</option><option>Sunglasses</option><option>Other</option>
        </SelectField>
      </Field>

      <Field label="Brand"><Input placeholder="e.g. Zara, H&M, Local Brand" value={f('brand')} onChange={set('brand')} /></Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Base Price (KES)" required><Input type="number" placeholder="1500" value={f('price')} onChange={set('price')} /></Field>
        <Field label="Condition" required>
          <SelectField value={f('condition')} onChange={set('condition')}>
            <option value="">Select</option><option>New</option><option>Like New</option><option>Good</option><option>Fair</option>
          </SelectField>
        </Field>
      </div>

      {/* Color Selection */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-600 text-foreground">Available Colors</label>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((c) =>
          <button key={c} onClick={() => toggleColor(c)}
          className="px-3 py-1.5 rounded-full text-xs font-600 transition-all duration-150"
          style={{
            background: selectedColors.includes(c) ? 'rgba(233,30,99,0.15)' : 'var(--secondary)',
            border: `1px solid ${selectedColors.includes(c) ? '#E91E63' : 'var(--border)'}`,
            color: selectedColors.includes(c) ? '#E91E63' : 'var(--muted-foreground)'
          }}>
              {c}
            </button>
          )}
        </div>
      </div>

      {/* Size/Color Variants */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-600 text-foreground">Size & Stock Variants</label>
          <button onClick={addVariant}
          className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700 transition-all active:scale-95"
          style={{ background: 'rgba(233,30,99,0.1)', color: '#E91E63', border: '1px solid rgba(233,30,99,0.3)' }}>
            <Plus size={12} /> Add Variant
          </button>
        </div>

        {variants.map((v, i) =>
        <div key={i} className="p-3 rounded-xl flex flex-col gap-2" style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-700 text-foreground">Variant {i + 1}</span>
              {variants.length > 1 &&
            <button onClick={() => removeVariant(i)} className="p-1 rounded-lg" style={{ color: 'var(--danger)' }}>
                  <X size={14} />
                </button>
            }
            </div>
            <div className="grid grid-cols-2 gap-2">
              <SelectField value={v.size} onChange={(e: any) => updateVariant(i, 'size', e.target.value)}>
                <option value="">Size</option>
                {SIZES_CLOTHING.map((s) => <option key={s}>{s}</option>)}
                {SIZES_SHOES.map((s) => <option key={s}>{s}</option>)}
                <option>One Size</option>
              </SelectField>
              <SelectField value={v.color} onChange={(e: any) => updateVariant(i, 'color', e.target.value)}>
                <option value="">Color</option>
                {COLORS.map((c) => <option key={c}>{c}</option>)}
              </SelectField>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Stock qty" value={v.stock} onChange={(e: any) => updateVariant(i, 'stock', e.target.value)} />
              <Input type="number" placeholder="Price (KES)" value={v.price} onChange={(e: any) => updateVariant(i, 'price', e.target.value)} />
            </div>
          </div>
        )}
      </div>

      <Field label="Description"><Textarea placeholder="Describe the product, material, fit, care instructions..." value={f('description')} onChange={set('description')} rows={3} /></Field>
      <Field label="Location" required><Input placeholder="e.g. Westlands, Nairobi" value={f('location')} onChange={set('location')} /></Field>
      <Field label="Delivery Available">
        <SelectField value={f('delivery')} onChange={set('delivery')}>
          <option value="">Select</option><option>Yes</option><option>No</option><option>Negotiable</option>
        </SelectField>
      </Field>

      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} max={6} label="Product Photos" />
    </div>);

}

// ─── DATING FORM ──────────────────────────────────────────────────────────────
function DatingForm({ data, onChange, images, onAddImage, onRemoveImage }: any) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: any) => onChange(key, e.target.value);

  const [verificationStep, setVerificationStep] = useState<'idle' | 'selfie' | 'verified'>('idle');
  const [interests, setInterests] = useState<string[]>([]);

  const interestOptions = [
  'Travel', 'Music', 'Cooking', 'Sports', 'Reading', 'Movies', 'Fitness',
  'Art', 'Photography', 'Dancing', 'Gaming', 'Hiking', 'Fashion', 'Tech'];


  const toggleInterest = (item: string) => {
    const updated = interests.includes(item) ? interests.filter((x) => x !== item) : [...interests, item];
    setInterests(updated);
    onChange('interests', updated.join(','));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(255,64,129,0.08)', border: '1px solid rgba(255,64,129,0.25)' }}>
        <Heart size={16} style={{ color: '#FF4081' }} />
        <span className="text-xs font-600" style={{ color: '#FF4081' }}>Dating Profile — Be authentic and respectful</span>
      </div>

      <Field label="Display Name" required hint="This is what others will see"><Input placeholder="e.g. Alex" value={f('displayName')} onChange={set('displayName')} /></Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Age" required><Input type="number" placeholder="25" value={f('age')} onChange={set('age')} /></Field>
        <Field label="Gender" required>
          <SelectField value={f('gender')} onChange={set('gender')}>
            <option value="">Select</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
          </SelectField>
        </Field>
      </div>

      <Field label="Looking For" required>
        <SelectField value={f('lookingFor')} onChange={set('lookingFor')}>
          <option value="">Select</option>
          <option>Friendship</option><option>Casual Dating</option><option>Serious Relationship</option>
          <option>Marriage</option><option>Networking</option>
        </SelectField>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Height (cm)"><Input type="number" placeholder="170" value={f('height')} onChange={set('height')} /></Field>
        <Field label="Body Type">
          <SelectField value={f('bodyType')} onChange={set('bodyType')}>
            <option value="">Select</option>
            <option>Slim</option><option>Athletic</option><option>Average</option><option>Curvy</option><option>Plus Size</option>
          </SelectField>
        </Field>
      </div>

      <Field label="Location" required><Input placeholder="e.g. Nairobi, Kenya" value={f('location')} onChange={set('location')} /></Field>

      <Field label="Occupation"><Input placeholder="e.g. Software Engineer" value={f('occupation')} onChange={set('occupation')} /></Field>

      <Field label="Education">
        <SelectField value={f('education')} onChange={set('education')}>
          <option value="">Select</option>
          <option>High School</option><option>Diploma</option><option>Bachelor's</option>
          <option>Master's</option><option>PhD</option><option>Other</option>
        </SelectField>
      </Field>

      <Field label="About Me" required>
        <Textarea placeholder="Tell people about yourself, your personality, what you enjoy..." value={f('bio')} onChange={set('bio')} rows={4} />
      </Field>

      {/* Interests */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-600 text-foreground">Interests (select all that apply)</label>
        <div className="flex flex-wrap gap-2">
          {interestOptions.map((item) =>
          <button key={item} onClick={() => toggleInterest(item)}
          className="px-3 py-1.5 rounded-full text-xs font-600 transition-all duration-150"
          style={{
            background: interests.includes(item) ? 'rgba(255,64,129,0.15)' : 'var(--secondary)',
            border: `1px solid ${interests.includes(item) ? '#FF4081' : 'var(--border)'}`,
            color: interests.includes(item) ? '#FF4081' : 'var(--muted-foreground)'
          }}>
              {item}
            </button>
          )}
        </div>
      </div>

      {/* Profile Photos */}
      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} max={6} label="Profile Photos" />

      {/* Photo Verification Flow */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-600 text-foreground">Photo Verification</label>
        <div className="p-4 rounded-xl flex flex-col gap-3"
        style={{ background: verificationStep === 'verified' ? 'rgba(0,200,83,0.06)' : 'var(--secondary)', border: `1px solid ${verificationStep === 'verified' ? 'var(--primary)' : 'var(--border)'}` }}>
          {verificationStep === 'idle' &&
          <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,64,129,0.1)' }}>
                  <Camera size={20} style={{ color: '#FF4081' }} />
                </div>
                <div>
                  <p className="text-sm font-700 text-foreground">Verify Your Identity</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Take a selfie to get a verified badge on your profile</p>
                </div>
              </div>
              <button onClick={() => setVerificationStep('selfie')}
            className="w-full py-2.5 rounded-xl text-sm font-700 transition-all active:scale-95"
            style={{ background: 'rgba(255,64,129,0.1)', color: '#FF4081', border: '1px solid rgba(255,64,129,0.3)' }}>
                Start Verification
              </button>
            </>
          }
          {verificationStep === 'selfie' &&
          <>
              <p className="text-sm font-700 text-foreground">📸 Take a Selfie</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                Hold up 2 fingers and look directly at the camera. Your selfie is compared to your profile photos to confirm authenticity.
              </p>
              <div className="w-full h-32 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.05)', border: '2px dashed var(--border)' }}>
                <div className="flex flex-col items-center gap-2">
                  <Camera size={32} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Camera preview</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setVerificationStep('idle')} className="flex-1 py-2 rounded-xl text-sm font-600"
              style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>
                  Cancel
                </button>
                <button onClick={() => {setVerificationStep('verified');onChange('photoVerified', 'true');}}
              className="flex-1 py-2 rounded-xl text-sm font-700"
              style={{ background: '#FF4081', color: '#fff' }}>
                  Submit Selfie
                </button>
              </div>
            </>
          }
          {verificationStep === 'verified' &&
          <div className="flex items-center gap-3">
              <CheckCircle size={24} style={{ color: 'var(--primary)' }} />
              <div>
                <p className="text-sm font-700 text-foreground">Identity Verified ✓</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Your profile will show a verified badge</p>
              </div>
            </div>
          }
        </div>
      </div>
    </div>);

}

// ─── BUSINESS DIRECTORY FORM ──────────────────────────────────────────────────
function BusinessDirectoryForm({ data, onChange, images, onAddImage, onRemoveImage }: any) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: any) => onChange(key, e.target.value);

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const [availability, setAvailability] = useState<Record<string, {open: string;close: string;closed: boolean;}>>(() => {
    const init: Record<string, {open: string;close: string;closed: boolean;}> = {};
    DAYS.forEach((d) => {init[d] = { open: '08:00', close: '17:00', closed: false };});
    init['Sat'] = { open: '09:00', close: '13:00', closed: false };
    init['Sun'] = { open: '', close: '', closed: true };
    return init;
  });

  const updateAvailability = (day: string, key: string, val: string | boolean) => {
    const updated = { ...availability, [day]: { ...availability[day], [key]: val } };
    setAvailability(updated);
    onChange('availability', JSON.stringify(updated));
  };

  return (
    <div className="flex flex-col gap-4">
      <Field label="Business Name" required><Input placeholder="e.g. Nairobi Tech Solutions" value={f('businessName')} onChange={set('businessName')} /></Field>

      <Field label="Business Category" required>
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select category</option>
          <option>IT & Technology</option><option>Legal Services</option><option>Accounting & Finance</option>
          <option>Healthcare</option><option>Education & Training</option><option>Construction</option>
          <option>Cleaning Services</option><option>Security</option><option>Logistics</option>
          <option>Marketing & PR</option><option>Catering</option><option>Consulting</option><option>Other</option>
        </SelectField>
      </Field>

      <Field label="Service Description" required>
        <Textarea placeholder="Describe your services, expertise, and what makes you unique..." value={f('description')} onChange={set('description')} rows={4} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Starting Price (KES)"><Input type="number" placeholder="5000" value={f('price')} onChange={set('price')} /></Field>
        <Field label="Price Type">
          <SelectField value={f('priceType')} onChange={set('priceType')}>
            <option value="">Select</option>
            <option>Per Hour</option><option>Per Day</option><option>Per Project</option><option>Monthly</option><option>Negotiable</option>
          </SelectField>
        </Field>
      </div>

      <Field label="Location" required><Input placeholder="e.g. Westlands, Nairobi" value={f('location')} onChange={set('location')} /></Field>
      <Field label="Website"><Input placeholder="https://yourbusiness.com" value={f('website')} onChange={set('website')} /></Field>
      <Field label="Phone" required><Input type="tel" placeholder="+254 700 000 000" value={f('phone')} onChange={set('phone')} /></Field>
      <Field label="Email"><Input type="email" placeholder="info@business.com" value={f('email')} onChange={set('email')} /></Field>

      {/* Availability Calendar */}
      <div className="flex flex-col gap-3">
        <label className="text-sm font-600 text-foreground">Availability Schedule</label>
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          {DAYS.map((day, i) =>
          <div key={day} className="flex items-center gap-3 px-3 py-2.5"
          style={{ borderBottom: i < DAYS.length - 1 ? '1px solid var(--border)' : 'none', background: availability[day].closed ? 'var(--secondary)' : 'var(--card)' }}>
              <span className="text-sm font-700 text-foreground w-8">{day}</span>
              {availability[day].closed ?
            <span className="text-xs flex-1" style={{ color: 'var(--muted-foreground)' }}>Closed</span> :

            <div className="flex items-center gap-2 flex-1">
                  <input type="time" value={availability[day].open}
              onChange={(e) => updateAvailability(day, 'open', e.target.value)}
              className="flex-1 px-2 py-1 rounded-lg text-xs outline-none"
              style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>–</span>
                  <input type="time" value={availability[day].close}
              onChange={(e) => updateAvailability(day, 'close', e.target.value)}
              className="flex-1 px-2 py-1 rounded-lg text-xs outline-none"
              style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
                </div>
            }
              <button onClick={() => updateAvailability(day, 'closed', !availability[day].closed)}
            className="text-xs font-600 px-2 py-1 rounded-lg transition-all"
            style={{
              background: availability[day].closed ? 'rgba(0,200,83,0.1)' : 'rgba(244,67,54,0.1)',
              color: availability[day].closed ? 'var(--primary)' : '#F44336',
              border: `1px solid ${availability[day].closed ? 'rgba(0,200,83,0.3)' : 'rgba(244,67,54,0.3)'}`
            }}>
                {availability[day].closed ? 'Open' : 'Close'}
              </button>
            </div>
          )}
        </div>
      </div>

      <Field label="Years in Business">
        <SelectField value={f('yearsInBusiness')} onChange={set('yearsInBusiness')}>
          <option value="">Select</option>
          <option>Less than 1 year</option><option>1-2 years</option><option>3-5 years</option>
          <option>6-10 years</option><option>10+ years</option>
        </SelectField>
      </Field>

      <Field label="Number of Employees">
        <SelectField value={f('employees')} onChange={set('employees')}>
          <option value="">Select</option>
          <option>Solo / Freelancer</option><option>2-5</option><option>6-20</option><option>21-50</option><option>50+</option>
        </SelectField>
      </Field>

      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} max={4} label="Business Photos / Logo" />
    </div>);

}

// ─── TRANSPORT FORM ───────────────────────────────────────────────────────────
function TransportForm({ data, onChange }: any) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: any) => onChange(key, e.target.value);

  const [stops, setStops] = useState<string[]>(['', '']);
  const addStop = () => setStops((prev) => [...prev, '']);
  const removeStop = (i: number) => setStops((prev) => prev.filter((_, idx) => idx !== i));
  const updateStop = (i: number, val: string) => {
    const updated = stops.map((s, idx) => idx === i ? val : s);
    setStops(updated);
    onChange('stops', updated.join(' → '));
  };

  const [schedules, setSchedules] = useState([{ departure: '', arrival: '', days: 'Daily' }]);
  const addSchedule = () => setSchedules((prev) => [...prev, { departure: '', arrival: '', days: 'Daily' }]);
  const removeSchedule = (i: number) => setSchedules((prev) => prev.filter((_, idx) => idx !== i));
  const updateSchedule = (i: number, key: string, val: string) => {
    const updated = schedules.map((s, idx) => idx === i ? { ...s, [key]: val } : s);
    setSchedules(updated);
    onChange('schedules', JSON.stringify(updated));
  };

  return (
    <div className="flex flex-col gap-4">
      <Field label="Transport Type" required>
        <SelectField value={f('transportType')} onChange={set('transportType')}>
          <option value="">Select</option>
          <option>Bus</option><option>Matatu</option><option>Taxi / Cab</option><option>Shuttle</option>
          <option>Boda Boda</option><option>Tuk Tuk</option><option>Train</option><option>Ferry</option>
        </SelectField>
      </Field>

      <Field label="Operator / Company Name"><Input placeholder="e.g. Modern Coast, Easy Coach" value={f('operator')} onChange={set('operator')} /></Field>

      {/* Route Builder */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-600 text-foreground">Route Stops <span style={{ color: 'var(--danger)' }}>*</span></label>
        {stops.map((stop, i) =>
        <div key={i} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
              <div className="w-3 h-3 rounded-full" style={{ background: i === 0 ? 'var(--primary)' : i === stops.length - 1 ? '#F44336' : 'var(--border)', border: '2px solid var(--border)' }} />
              {i < stops.length - 1 && <div className="w-0.5 h-4" style={{ background: 'var(--border)' }} />}
            </div>
            <Input
            placeholder={i === 0 ? 'Origin (e.g. Nairobi CBD)' : i === stops.length - 1 ? 'Destination (e.g. Mombasa)' : `Stop ${i} (e.g. Mtito Andei)`}
            value={stop}
            onChange={(e: any) => updateStop(i, e.target.value)} />
          
            {stops.length > 2 && i > 0 && i < stops.length - 1 &&
          <button onClick={() => removeStop(i)} className="p-2 rounded-xl flex-shrink-0" style={{ color: 'var(--danger)' }}>
                <X size={16} />
              </button>
          }
          </div>
        )}
        <button onClick={addStop}
        className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-600 transition-all active:scale-95 self-start"
        style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>
          <Plus size={14} /> Add Stop
        </button>
      </div>

      {/* Schedule Builder */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-600 text-foreground">Departure Schedules <span style={{ color: 'var(--danger)' }}>*</span></label>
          <button onClick={addSchedule}
          className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700 transition-all active:scale-95"
          style={{ background: 'rgba(96,125,139,0.1)', color: '#607D8B', border: '1px solid rgba(96,125,139,0.3)' }}>
            <Plus size={12} /> Add Time
          </button>
        </div>
        {schedules.map((s, i) =>
        <div key={i} className="p-3 rounded-xl flex flex-col gap-2" style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-700 text-foreground">Schedule {i + 1}</span>
              {schedules.length > 1 &&
            <button onClick={() => removeSchedule(i)} style={{ color: 'var(--danger)' }}><X size={14} /></button>
            }
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--muted-foreground)' }}>Departure</p>
                <input type="time" value={s.departure} onChange={(e) => updateSchedule(i, 'departure', e.target.value)}
              className="w-full px-3 py-2 rounded-xl text-sm outline-none"
              style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
              </div>
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--muted-foreground)' }}>Arrival (est.)</p>
                <input type="time" value={s.arrival} onChange={(e) => updateSchedule(i, 'arrival', e.target.value)}
              className="w-full px-3 py-2 rounded-xl text-sm outline-none"
              style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
              </div>
            </div>
            <SelectField value={s.days} onChange={(e: any) => updateSchedule(i, 'days', e.target.value)}>
              <option>Daily</option><option>Weekdays Only</option><option>Weekends Only</option>
              <option>Mon-Sat</option><option>Specific Days</option>
            </SelectField>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Price (KES)" required><Input type="number" placeholder="500" value={f('price')} onChange={set('price')} /></Field>
        <Field label="Available Seats"><Input type="number" placeholder="14" value={f('seats')} onChange={set('seats')} /></Field>
      </div>

      <Field label="Vehicle Registration"><Input placeholder="e.g. KCA 123A" value={f('registration')} onChange={set('registration')} /></Field>
      <Field label="Contact" required><Input type="tel" placeholder="+254 700 000 000" value={f('contact')} onChange={set('contact')} /></Field>
      <Field label="Additional Notes"><Textarea placeholder="Luggage policy, booking instructions, special stops..." value={f('notes')} onChange={set('notes')} rows={2} /></Field>
    </div>);

}

// ─── VIDEO FORM ───────────────────────────────────────────────────────────────
function VideoForm({ data, onChange, images, onAddImage, onRemoveImage }: any) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: any) => onChange(key, e.target.value);
  const [videoUploaded, setVideoUploaded] = useState(false);
  const [thumbnailUploaded, setThumbnailUploaded] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const simulateUpload = () => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {clearInterval(interval);setVideoUploaded(true);onChange('videoUploaded', 'true');return 100;}
        return prev + 10;
      });
    }, 200);
  };

  return (
    <div className="flex flex-col gap-4">
      <Field label="Video Title" required><Input placeholder="e.g. How to Start a Business in Kenya" value={f('title')} onChange={set('title')} /></Field>

      <Field label="Category" required>
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select</option>
          <option>Education</option><option>Entertainment</option><option>News & Current Affairs</option>
          <option>Sports</option><option>Business</option><option>Lifestyle</option>
          <option>Music</option><option>Comedy</option><option>Technology</option><option>Travel</option>
        </SelectField>
      </Field>

      {/* Video Upload */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-600 text-foreground">Video File <span style={{ color: 'var(--danger)' }}>*</span></label>
        <div className="p-4 rounded-xl flex flex-col items-center gap-3 text-center"
        style={{ border: `2px dashed ${videoUploaded ? 'var(--primary)' : 'var(--border)'}`, background: videoUploaded ? 'rgba(0,200,83,0.05)' : 'var(--secondary)' }}>
          {videoUploaded ?
          <>
              <CheckCircle size={28} style={{ color: 'var(--primary)' }} />
              <div>
                <p className="text-sm font-700 text-foreground">Video Uploaded</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>video_file.mp4 — Processing...</p>
              </div>
              <button onClick={() => {setVideoUploaded(false);setUploadProgress(0);onChange('videoUploaded', '');}}
            className="text-xs font-600" style={{ color: 'var(--danger)' }}>Remove</button>
            </> :
          uploadProgress > 0 ?
          <>
              <div className="w-full h-2 rounded-full" style={{ background: 'var(--border)' }}>
                <div className="h-full rounded-full transition-all duration-200" style={{ width: `${uploadProgress}%`, background: 'var(--primary)' }} />
              </div>
              <p className="text-sm font-600 text-foreground">Uploading... {uploadProgress}%</p>
            </> :

          <>
              <Video size={28} style={{ color: 'var(--muted-foreground)' }} />
              <div>
                <p className="text-sm font-600 text-foreground">Upload Video File</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>MP4, MOV, AVI — max 500MB</p>
              </div>
              <button onClick={simulateUpload}
            className="px-4 py-2 rounded-xl text-sm font-700 transition-all active:scale-95"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                Choose Video
              </button>
            </>
          }
        </div>
      </div>

      {/* OR Video URL */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
        <span className="text-xs font-600" style={{ color: 'var(--muted-foreground)' }}>OR paste URL</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
      </div>
      <Field label="Video URL (YouTube / Vimeo)">
        <Input placeholder="https://youtube.com/watch?v=..." value={f('videoUrl')} onChange={set('videoUrl')} />
      </Field>

      {/* Thumbnail Upload */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-600 text-foreground">Thumbnail Image</label>
        <div className="flex items-center gap-3">
          {thumbnailUploaded ?
          <div className="relative w-32 h-20 rounded-xl overflow-hidden flex-shrink-0" style={{ border: '2px solid var(--primary)' }}>
              <img src="https://img.rocket.new/generatedImages/rocket_gen_img_18340dbd7-1768512878569.png" alt="Thumbnail" className="w-full h-full object-cover" />
              <button onClick={() => setThumbnailUploaded(false)}
            className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.7)' }}>
                <X size={10} style={{ color: '#fff' }} />
              </button>
            </div> :

          <button onClick={() => {setThumbnailUploaded(true);onChange('thumbnailUploaded', 'true');}}
          className="w-32 h-20 rounded-xl flex flex-col items-center justify-center gap-1 flex-shrink-0 transition-all active:scale-95"
          style={{ border: '2px dashed var(--border)', background: 'var(--secondary)' }}>
              <Camera size={20} style={{ color: 'var(--muted-foreground)' }} />
              <span className="text-[9px] font-600" style={{ color: 'var(--muted-foreground)' }}>Add Thumbnail</span>
            </button>
          }
          <div className="flex-1">
            <p className="text-xs font-600 text-foreground mb-1">Custom Thumbnail</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Recommended: 1280×720px (16:9). JPG or PNG. Max 2MB.</p>
          </div>
        </div>
      </div>

      <Field label="Description"><Textarea placeholder="Describe your video content..." value={f('description')} onChange={set('description')} rows={3} /></Field>

      <Field label="Tags"><Input placeholder="e.g. business, kenya, startup (comma separated)" value={f('tags')} onChange={set('tags')} /></Field>

      <Field label="Visibility" required>
        <SelectField value={f('visibility')} onChange={set('visibility')}>
          <option value="">Select</option><option>Public</option><option>Unlisted</option><option>Private</option>
        </SelectField>
      </Field>

      <Field label="Monetization">
        <SelectField value={f('monetization')} onChange={set('monetization')}>
          <option value="">Select</option>
          <option>Free</option><option>Ad-supported</option><option>Premium (paid)</option>
        </SelectField>
      </Field>
    </div>);

}

// ─── NEWS FORM ────────────────────────────────────────────────────────────────
function NewsForm({ data, onChange, images, onAddImage, onRemoveImage }: any) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: any) => onChange(key, e.target.value);
  const [wordCount, setWordCount] = useState(0);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    onChange('content', val);
    setWordCount(val.trim() ? val.trim().split(/\s+/).length : 0);
  };

  const [formatting, setFormatting] = useState({ bold: false, italic: false, quote: false });

  return (
    <div className="flex flex-col gap-4">
      <Field label="Headline" required hint="Keep it clear, factual, and under 100 characters">
        <Input placeholder="e.g. Kenya Tech Sector Grows 40% in 2026" value={f('title')} onChange={set('title')} />
        <p className="text-xs text-right" style={{ color: f('title').length > 80 ? '#F44336' : 'var(--muted-foreground)' }}>
          {f('title').length}/100
        </p>
      </Field>

      <Field label="Sub-headline / Summary">
        <Input placeholder="Brief summary of the article (shown in previews)" value={f('summary')} onChange={set('summary')} />
      </Field>

      <Field label="Category" required>
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select</option>
          <option>Business & Economy</option><option>Technology</option><option>Politics</option>
          <option>Sports</option><option>Entertainment</option><option>Health</option>
          <option>Education</option><option>Environment</option><option>Crime & Justice</option>
          <option>International</option><option>Opinion</option>
        </SelectField>
      </Field>

      {/* Article Editor */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-600 text-foreground">Article Body <span style={{ color: 'var(--danger)' }}>*</span></label>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{wordCount} words</span>
        </div>

        {/* Formatting Toolbar */}
        <div className="flex items-center gap-1 p-2 rounded-t-xl" style={{ background: 'var(--secondary)', border: '2px solid var(--border)', borderBottom: 'none' }}>
          {[
          { key: 'bold', label: 'B', style: { fontWeight: 700 } },
          { key: 'italic', label: 'I', style: { fontStyle: 'italic' } },
          { key: 'quote', label: '❝', style: {} }].
          map((btn) =>
          <button key={btn.key}
          onClick={() => setFormatting((prev) => ({ ...prev, [btn.key]: !prev[btn.key as keyof typeof prev] }))}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-all"
          style={{
            ...btn.style,
            background: formatting[btn.key as keyof typeof formatting] ? 'var(--primary)' : 'transparent',
            color: formatting[btn.key as keyof typeof formatting] ? 'var(--primary-foreground)' : 'var(--foreground)'
          }}>
              {btn.label}
            </button>
          )}
          <div className="w-px h-5 mx-1" style={{ background: 'var(--border)' }} />
          <span className="text-xs px-2" style={{ color: 'var(--muted-foreground)' }}>H1</span>
          <span className="text-xs px-2" style={{ color: 'var(--muted-foreground)' }}>H2</span>
          <span className="text-xs px-2" style={{ color: 'var(--muted-foreground)' }}>• List</span>
        </div>
        <textarea
          placeholder="Write your full article here. Include facts, quotes, context, and sources. Minimum 200 words recommended..."
          value={f('content')}
          onChange={handleContentChange}
          rows={12}
          className="w-full px-4 py-3 text-sm font-500 text-foreground placeholder-muted-foreground outline-none transition-all duration-200 resize-none rounded-b-xl"
          style={{ background: 'var(--input)', border: '2px solid var(--border)', borderTop: 'none' }} />
        
        {wordCount > 0 && wordCount < 100 &&
        <p className="text-xs" style={{ color: '#FF9800' }}>⚠ Minimum 100 words recommended for quality articles</p>
        }
      </div>

      <Field label="Author Name" required><Input placeholder="e.g. John Doe" value={f('author')} onChange={set('author')} /></Field>
      <Field label="Author Title / Role"><Input placeholder="e.g. Senior Reporter, China Ndogo News" value={f('authorTitle')} onChange={set('authorTitle')} /></Field>

      <Field label="Source / Reference URL"><Input placeholder="https://source.com/original-article" value={f('sourceUrl')} onChange={set('sourceUrl')} /></Field>

      <Field label="Tags / Keywords"><Input placeholder="e.g. kenya, economy, tech (comma separated)" value={f('tags')} onChange={set('tags')} /></Field>

      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} max={3} label="Featured Image" />

      <Field label="Publish Status">
        <SelectField value={f('publishStatus')} onChange={set('publishStatus')}>
          <option value="">Select</option>
          <option>Publish Now</option><option>Save as Draft</option><option>Schedule</option>
        </SelectField>
      </Field>

      {f('publishStatus') === 'Schedule' &&
      <div className="grid grid-cols-2 gap-3">
          <Field label="Publish Date"><Input type="date" value={f('publishDate')} onChange={set('publishDate')} /></Field>
          <Field label="Publish Time"><Input type="time" value={f('publishTime')} onChange={set('publishTime')} /></Field>
        </div>
      }
    </div>);

}

// ─── GENERIC FORMS (existing) ─────────────────────────────────────────────────
function GenericListingForm({ type, data, onChange, images, onAddImage, onRemoveImage






}: {type: ListingType;data: Record<string, string>;onChange: (key: string, val: string) => void;images: string[];onAddImage: (url: string) => void;onRemoveImage: (i: number) => void;}) {
  const f = (key: string) => data[key] || '';
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(key, e.target.value);

  if (type === 'product' || type === 'classified') return (
    <div className="flex flex-col gap-4">
      <Field label="Title" required><Input placeholder="e.g. Samsung Galaxy S24 Ultra" value={f('title')} onChange={set('title')} /></Field>
      <Field label="Category" required>
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select category</option>
          <option>Electronics</option><option>Clothing</option><option>Furniture</option>
          <option>Vehicles</option><option>Books</option><option>Sports</option><option>Other</option>
        </SelectField>
      </Field>
      <Field label="Condition" required>
        <SelectField value={f('condition')} onChange={set('condition')}>
          <option value="">Select condition</option>
          <option>New</option><option>Like New</option><option>Good</option><option>Fair</option><option>For Parts</option>
        </SelectField>
      </Field>
      <Field label="Price (KES)" required><Input type="number" placeholder="0" value={f('price')} onChange={set('price')} /></Field>
      <Field label="Description" required><Textarea placeholder="Describe your item..." value={f('description')} onChange={set('description')} /></Field>
      <Field label="Location" required><Input placeholder="e.g. Westlands, Nairobi" value={f('location')} onChange={set('location')} /></Field>
      <Field label="Delivery Available">
        <SelectField value={f('delivery')} onChange={set('delivery')}>
          <option value="">Select</option><option>Yes</option><option>No</option><option>Negotiable</option>
        </SelectField>
      </Field>
      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} />
    </div>);


  if (type === 'vehicle') return (
    <div className="flex flex-col gap-4">
      <Field label="Vehicle Type" required>
        <SelectField value={f('vehicleType')} onChange={set('vehicleType')}>
          <option value="">Select type</option>
          <option>Car</option><option>SUV</option><option>Truck</option><option>Motorcycle</option><option>Bus</option><option>Van</option>
        </SelectField>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Brand" required><Input placeholder="Toyota" value={f('brand')} onChange={set('brand')} /></Field>
        <Field label="Model" required><Input placeholder="Land Cruiser" value={f('model')} onChange={set('model')} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Year" required><Input type="number" placeholder="2020" value={f('year')} onChange={set('year')} /></Field>
        <Field label="Mileage (km)"><Input type="number" placeholder="50000" value={f('mileage')} onChange={set('mileage')} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Fuel Type">
          <SelectField value={f('fuel')} onChange={set('fuel')}>
            <option value="">Select</option><option>Petrol</option><option>Diesel</option><option>Electric</option><option>Hybrid</option>
          </SelectField>
        </Field>
        <Field label="Transmission">
          <SelectField value={f('transmission')} onChange={set('transmission')}>
            <option value="">Select</option><option>Automatic</option><option>Manual</option>
          </SelectField>
        </Field>
      </div>
      <Field label="Condition">
        <SelectField value={f('condition')} onChange={set('condition')}>
          <option value="">Select</option><option>New</option><option>Foreign Used</option><option>Locally Used</option>
        </SelectField>
      </Field>
      <Field label="Price (KES)" required><Input type="number" placeholder="0" value={f('price')} onChange={set('price')} /></Field>
      <Field label="Location" required><Input placeholder="e.g. Westlands, Nairobi" value={f('location')} onChange={set('location')} /></Field>
      <Field label="Description"><Textarea placeholder="Additional details about the vehicle..." value={f('description')} onChange={set('description')} rows={3} /></Field>
      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} />
    </div>);


  if (type === 'property') return (
    <div className="flex flex-col gap-4">
      <Field label="Property Type" required>
        <SelectField value={f('propertyType')} onChange={set('propertyType')}>
          <option value="">Select type</option>
          <option>Apartment</option><option>House</option><option>Land</option><option>Commercial</option><option>Office</option>
        </SelectField>
      </Field>
      <Field label="Listing Type" required>
        <SelectField value={f('listingType')} onChange={set('listingType')}>
          <option value="">Select</option><option>For Sale</option><option>For Rent</option><option>Short Stay</option>
        </SelectField>
      </Field>
      <Field label="Title" required><Input placeholder="e.g. 3BR Apartment Karen Estate" value={f('title')} onChange={set('title')} /></Field>
      <Field label="Price (KES)" required><Input type="number" placeholder="0" value={f('price')} onChange={set('price')} /></Field>
      <Field label="Location" required><Input placeholder="e.g. Karen, Nairobi" value={f('location')} onChange={set('location')} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Bedrooms"><Input type="number" placeholder="3" value={f('bedrooms')} onChange={set('bedrooms')} /></Field>
        <Field label="Bathrooms"><Input type="number" placeholder="2" value={f('bathrooms')} onChange={set('bathrooms')} /></Field>
      </div>
      <Field label="Description"><Textarea placeholder="Describe the property..." value={f('description')} onChange={set('description')} /></Field>
      <Field label="Amenities"><Input placeholder="e.g. Pool, Gym, Parking, Security" value={f('amenities')} onChange={set('amenities')} /></Field>
      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} />
    </div>);


  if (type === 'job') return (
    <div className="flex flex-col gap-4">
      <Field label="Job Title" required><Input placeholder="e.g. Senior Software Engineer" value={f('title')} onChange={set('title')} /></Field>
      <Field label="Company Name" required><Input placeholder="e.g. Safaricom PLC" value={f('company')} onChange={set('company')} /></Field>
      <Field label="Category" required>
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select category</option>
          <option>Technology</option><option>Finance</option><option>Healthcare</option><option>Education</option>
          <option>Sales</option><option>Marketing</option><option>Engineering</option><option>Other</option>
        </SelectField>
      </Field>
      <Field label="Job Type">
        <SelectField value={f('jobType')} onChange={set('jobType')}>
          <option value="">Select</option><option>Full-time</option><option>Part-time</option><option>Contract</option><option>Remote</option>
        </SelectField>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Min Salary (KES)"><Input type="number" placeholder="50000" value={f('salaryMin')} onChange={set('salaryMin')} /></Field>
        <Field label="Max Salary (KES)"><Input type="number" placeholder="150000" value={f('salaryMax')} onChange={set('salaryMax')} /></Field>
      </div>
      <Field label="Location" required><Input placeholder="e.g. Westlands, Nairobi" value={f('location')} onChange={set('location')} /></Field>
      <Field label="Requirements"><Textarea placeholder="List key requirements..." value={f('requirements')} onChange={set('requirements')} /></Field>
      <Field label="Description"><Textarea placeholder="Describe the role and responsibilities..." value={f('description')} onChange={set('description')} /></Field>
      <Field label="Application Deadline"><Input type="date" value={f('deadline')} onChange={set('deadline')} /></Field>
      <Field label="Application Method">
        <SelectField value={f('applyMethod')} onChange={set('applyMethod')}>
          <option value="">Select</option><option>Apply on Platform</option><option>Email</option><option>WhatsApp</option>
        </SelectField>
      </Field>
    </div>);


  if (type === 'hotel') return (
    <div className="flex flex-col gap-4">
      <Field label="Hotel Name" required><Input placeholder="e.g. Sarova Stanley" value={f('name')} onChange={set('name')} /></Field>
      <Field label="Location" required><Input placeholder="e.g. CBD, Nairobi" value={f('location')} onChange={set('location')} /></Field>
      <Field label="Star Rating">
        <SelectField value={f('stars')} onChange={set('stars')}>
          <option value="">Select</option><option>1 Star</option><option>2 Stars</option><option>3 Stars</option><option>4 Stars</option><option>5 Stars</option>
        </SelectField>
      </Field>
      <Field label="Price Per Night (KES)" required><Input type="number" placeholder="5000" value={f('price')} onChange={set('price')} /></Field>
      <Field label="Number of Rooms"><Input type="number" placeholder="50" value={f('rooms')} onChange={set('rooms')} /></Field>
      <Field label="Amenities"><Input placeholder="e.g. Pool, WiFi, Restaurant, Gym" value={f('amenities')} onChange={set('amenities')} /></Field>
      <Field label="Description"><Textarea placeholder="Describe your hotel..." value={f('description')} onChange={set('description')} /></Field>
      <Field label="Contact Phone"><Input type="tel" placeholder="+254 700 000 000" value={f('phone')} onChange={set('phone')} /></Field>
      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} />
    </div>);


  if (type === 'event') return (
    <div className="flex flex-col gap-4">
      <Field label="Event Title" required><Input placeholder="e.g. Nairobi Tech Summit 2026" value={f('title')} onChange={set('title')} /></Field>
      <Field label="Category">
        <SelectField value={f('category')} onChange={set('category')}>
          <option value="">Select</option><option>Conference</option><option>Concert</option><option>Sports</option>
          <option>Workshop</option><option>Exhibition</option><option>Festival</option>
        </SelectField>
      </Field>
      <Field label="Venue" required><Input placeholder="e.g. KICC, Nairobi" value={f('venue')} onChange={set('venue')} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date" required><Input type="date" value={f('date')} onChange={set('date')} /></Field>
        <Field label="Time" required><Input type="time" value={f('time')} onChange={set('time')} /></Field>
      </div>
      <Field label="Ticket Price (KES)"><Input type="number" placeholder="0 for free" value={f('ticketPrice')} onChange={set('ticketPrice')} /></Field>
      <Field label="Description"><Textarea placeholder="Describe the event..." value={f('description')} onChange={set('description')} /></Field>
      <Field label="Organiser Name"><Input placeholder="e.g. Tech Events Kenya" value={f('organiser')} onChange={set('organiser')} /></Field>
      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} max={3} />
    </div>);


  if (type === 'service') return (
    <div className="flex flex-col gap-4">
      <Field label="Service Type" required>
        <SelectField value={f('serviceType')} onChange={set('serviceType')}>
          <option value="">Select type</option>
          <option>Plumbing</option><option>Electrical</option><option>Cleaning</option><option>IT Support</option>
          <option>Tutoring</option><option>Design</option><option>Photography</option><option>Other</option>
        </SelectField>
      </Field>
      <Field label="Title" required><Input placeholder="e.g. Professional House Cleaning" value={f('title')} onChange={set('title')} /></Field>
      <Field label="Price (KES)" required><Input type="number" placeholder="1000" value={f('price')} onChange={set('price')} /></Field>
      <Field label="Location" required><Input placeholder="e.g. Nairobi, Kenya" value={f('location')} onChange={set('location')} /></Field>
      <Field label="Availability"><Input placeholder="e.g. Mon-Fri 8am-6pm" value={f('availability')} onChange={set('availability')} /></Field>
      <Field label="Description"><Textarea placeholder="Describe your service..." value={f('description')} onChange={set('description')} /></Field>
      <ImageUploadArea images={images} onAdd={onAddImage} onRemove={onRemoveImage} max={4} />
    </div>);


  return null;
}

// ─── MASTER FORM ROUTER ───────────────────────────────────────────────────────
function ListingForm({ type, data, onChange, images, onAddImage, onRemoveImage






}: {type: ListingType;data: Record<string, string>;onChange: (key: string, val: string) => void;images: string[];onAddImage: (url: string) => void;onRemoveImage: (i: number) => void;}) {
  if (type === 'pharmacy') return <PharmacyForm data={data} onChange={onChange} images={images} onAddImage={onAddImage} onRemoveImage={onRemoveImage} />;
  if (type === 'food') return <FoodForm data={data} onChange={onChange} images={images} onAddImage={onAddImage} onRemoveImage={onRemoveImage} />;
  if (type === 'beauty_fashion') return <BeautyFashionForm data={data} onChange={onChange} images={images} onAddImage={onAddImage} onRemoveImage={onRemoveImage} />;
  if (type === 'dating') return <DatingForm data={data} onChange={onChange} images={images} onAddImage={onAddImage} onRemoveImage={onRemoveImage} />;
  if (type === 'business_directory') return <BusinessDirectoryForm data={data} onChange={onChange} images={images} onAddImage={onAddImage} onRemoveImage={onRemoveImage} />;
  if (type === 'transport') return <TransportForm data={data} onChange={onChange} />;
  if (type === 'video') return <VideoForm data={data} onChange={onChange} images={images} onAddImage={onAddImage} onRemoveImage={onRemoveImage} />;
  if (type === 'news') return <NewsForm data={data} onChange={onChange} images={images} onAddImage={onAddImage} onRemoveImage={onRemoveImage} />;
  return <GenericListingForm type={type} data={data} onChange={onChange} images={images} onAddImage={onAddImage} onRemoveImage={onRemoveImage} />;
}

function PreviewCard({ type, data, images }: {type: ListingType;data: Record<string, string>;images: string[];}) {
  const cat = LISTING_CATEGORIES.find((c) => c.id === type);

  const getTitle = () => data.title || data.name || data.displayName || data.businessName || data.headline || 'Untitled';
  const getSubtitle = () => {
    if (type === 'pharmacy') return `${data.pharmacyName || ''} • ${data.prescriptionRequired === 'true' ? '🩺 Prescription Required' : '✓ OTC'}`;
    if (type === 'food') return `${data.restaurant || ''} • ${data.prepTime ? `${data.prepTime} min prep` : ''}`;
    if (type === 'beauty_fashion') return `${data.brand || ''} • ${data.availableColors || ''}`;
    if (type === 'dating') return `${data.age ? `Age ${data.age}` : ''} • ${data.location || ''} ${data.photoVerified === 'true' ? '✓ Verified' : ''}`;
    if (type === 'business_directory') return `${data.category || ''} • ${data.location || ''}`;
    if (type === 'transport') return `${data.stops || data.route || ''} • ${data.transportType || ''}`;
    if (type === 'video') return `${data.category || ''} • ${data.visibility || 'Public'}`;
    if (type === 'news') return `${data.category || ''} • ${data.author || ''}`;
    return data.location || '';
  };

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--card)' }}>
      {images[0] &&
      <img src={images[0]} alt="Preview" className="w-full h-44 object-cover" />
      }
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-700"
          style={{ background: `${cat?.color}20`, color: cat?.color }}>
            {cat?.label}
          </span>
          {type === 'pharmacy' && data.prescriptionRequired === 'true' &&
          <span className="px-2 py-0.5 rounded-full text-[10px] font-700" style={{ background: 'rgba(255,152,0,0.15)', color: '#FF9800' }}>
              Rx Required
            </span>
          }
          {type === 'dating' && data.photoVerified === 'true' &&
          <span className="px-2 py-0.5 rounded-full text-[10px] font-700" style={{ background: 'rgba(0,200,83,0.15)', color: 'var(--primary)' }}>
              ✓ Verified
            </span>
          }
        </div>
        <h3 className="text-base font-700 text-foreground">{getTitle()}</h3>
        {getSubtitle() &&
        <p className="text-xs font-600" style={{ color: 'var(--primary)' }}>{getSubtitle()}</p>
        }
        {data.price &&
        <p className="text-sm font-700" style={{ color: 'var(--primary)' }}>
            KES {Number(data.price).toLocaleString()}
          </p>
        }
        {data.location &&
        <div className="flex items-center gap-1">
            <MapPin size={12} style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{data.location}</span>
          </div>
        }
        {(data.description || data.bio || data.content) &&
        <p className="text-xs line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>
            {data.description || data.bio || data.content}
          </p>
        }
        {type === 'beauty_fashion' && data.variants && (() => {
          try {
            const variants = JSON.parse(data.variants);
            return variants.length > 0 &&
            <div className="flex flex-wrap gap-1 mt-1">
                {variants.slice(0, 4).map((v: any, i: number) =>
              <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-600"
              style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>
                    {v.size} {v.color}
                  </span>
              )}
              </div>;

          } catch {return null;}
        })()}
      </div>
    </div>);

}

// ─── Inner component that uses useSearchParams ────────────────────────────────
function CreateListingInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, activeRole } = useAuth();

  const preselectedType = searchParams.get('type') as ListingType | null;

  const [step, setStep] = useState<'choose' | 'form' | 'preview' | 'success'>(
    preselectedType ? 'form' : 'choose'
  );
  const [selectedType, setSelectedType] = useState<ListingType | null>(preselectedType);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [images, setImages] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const recommendedTypes = getRecommendedTypes(activeRole || 'buyer');
  const recommended = LISTING_CATEGORIES.filter((c) => recommendedTypes.includes(c.id));
  const others = LISTING_CATEGORIES.filter((c) => !recommendedTypes.includes(c.id));

  const STEPS = ['Choose Type', 'Fill Details', 'Preview', 'Published'];
  const stepIndex = ['choose', 'form', 'preview', 'success'].indexOf(step);

  const handleSelect = (type: ListingType) => {
    setSelectedType(type);
    setFormData({});
    setImages([]);
    setStep('form');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    setStep('success');
  };

  const cat = LISTING_CATEGORIES.find((c) => c.id === selectedType);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: 'var(--background)' }}>
        <div className="max-w-2xl mx-auto px-4 py-6 pb-28">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {step !== 'choose' && step !== 'success' &&
              <button onClick={() => setStep(step === 'preview' ? 'form' : 'choose')}
              className="p-2 rounded-xl transition-all duration-150 active:scale-95"
              style={{ border: '1px solid var(--border)', background: 'var(--secondary)' }}>
                  <ChevronLeft size={18} style={{ color: 'var(--muted-foreground)' }} />
                </button>
              }
              <div>
                <h1 className="text-xl font-800 text-foreground">
                  {step === 'choose' ? 'What would you like to post?' :
                  step === 'form' ? `Post: ${cat?.label}` :
                  step === 'preview' ? 'Preview your listing' : 'Successfully Published!'}
                </h1>
                {step === 'choose' && activeRole &&
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    Role: <span className="font-600 capitalize" style={{ color: 'var(--primary)' }}>{activeRole.replace('_', ' ')}</span>
                  </p>
                }
              </div>
            </div>
            <HomeButton />
          </div>

          {step !== 'success' &&
          <StepProgress steps={STEPS} current={stepIndex} />
          }

          {/* Step 1: Choose Type */}
          {step === 'choose' &&
          <div className="flex flex-col gap-5">
              <div>
                <p className="text-xs font-700 uppercase tracking-wider mb-3" style={{ color: 'var(--primary)' }}>
                  ⭐ Recommended for you
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {recommended.map((c) =>
                <button key={c.id} onClick={() => handleSelect(c.id)}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl text-center transition-all duration-150 active:scale-95 hover:opacity-90"
                style={{ background: `${c.color}12`, border: `1.5px solid ${c.color}30` }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${c.color}20`, color: c.color }}>
                        {c.icon}
                      </div>
                      <span className="text-sm font-700 text-foreground">{c.label}</span>
                      <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{c.description}</span>
                    </button>
                )}
                </div>
              </div>
              {others.length > 0 &&
            <div>
                  <p className="text-xs font-700 uppercase tracking-wider mb-3" style={{ color: 'var(--muted-foreground)' }}>
                    More options
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {others.map((c) =>
                <button key={c.id} onClick={() => handleSelect(c.id)}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl text-center transition-all duration-150 active:scale-95 hover:opacity-90"
                style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${c.color}15`, color: c.color }}>
                          {c.icon}
                        </div>
                        <span className="text-sm font-700 text-foreground">{c.label}</span>
                        <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{c.description}</span>
                      </button>
                )}
                  </div>
                </div>
            }
            </div>
          }

          {/* Step 2: Form */}
          {step === 'form' && selectedType &&
          <div className="flex flex-col gap-5">
              <ListingForm
              type={selectedType}
              data={formData}
              onChange={(k, v) => setFormData((prev) => ({ ...prev, [k]: v }))}
              images={images}
              onAddImage={(url) => setImages((prev) => [...prev, url])}
              onRemoveImage={(i) => setImages((prev) => prev.filter((_, idx) => idx !== i))} />
            
              <button onClick={() => setStep('preview')}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-700 text-sm glow-green"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                Preview Listing <ChevronRight size={18} />
              </button>
            </div>
          }

          {/* Step 3: Preview */}
          {step === 'preview' && selectedType &&
          <div className="flex flex-col gap-5">
              <PreviewCard type={selectedType} data={formData} images={images} />
              <div className="p-4 rounded-2xl" style={{ background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.2)' }}>
                <p className="text-sm font-600 text-foreground mb-1">✅ Ready to publish</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {selectedType === 'pharmacy' ? 'Your listing will be reviewed by our team before going live (PPB compliance check).' : 'Your listing will be visible to all users immediately after publishing.'}
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep('form')}
              className="flex-1 py-4 rounded-2xl font-700 text-sm"
              style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>
                  Edit
                </button>
                <button onClick={handleSubmit} disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-700 text-sm disabled:opacity-60 glow-green"
              style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <><Send size={18} /> Publish</>}
                </button>
              </div>
            </div>
          }

          {/* Step 4: Success */}
          {step === 'success' &&
          <div className="flex flex-col items-center gap-6 text-center py-8">
              <div className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,200,83,0.12)', border: '2px solid var(--primary)' }}>
                <CheckCircle size={40} style={{ color: 'var(--primary)' }} />
              </div>
              <div>
                <h2 className="text-2xl font-800 text-foreground mb-2">Published!</h2>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  Your {cat?.label?.toLowerCase() || 'listing'} is now live and visible to all users.
                </p>
              </div>
              <div className="w-full flex flex-col gap-3">
                <button onClick={() => {setStep('choose');setSelectedType(null);setFormData({});setImages([]);}}
              className="w-full py-4 rounded-2xl font-700 text-sm glow-green"
              style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  Post Another
                </button>
                <button onClick={() => router.push('/dashboard')}
              className="w-full py-4 rounded-2xl font-700 text-sm"
              style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                  Go to Dashboard
                </button>
                <HomeButton className="w-full justify-center py-4" label="Back to Home" />
              </div>
            </div>
          }
        </div>
      </div>
    </AppLayout>);

}

// ─── Main export with Suspense boundary ──────────────────────────────────────
export default function CreateListingPage() {
  return (
    <Suspense fallback={
    <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading...</p>
          </div>
        </div>
      </AppLayout>
    }>
      <CreateListingInner />
    </Suspense>);

}